import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Paper,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useForm, Controller } from 'react-hook-form';
import DataTable from '../../components/common/DataTable';
import { useOrgUsers, useCreateOrgUser, useUpdateOrgUser, useDeleteOrgUser } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import type { User } from '../../types';

const allMenuItems = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'trailers', label: 'Køretøjer' },
  { key: 'calendar', label: 'Kalender' },
  { key: 'koresedler', label: 'Dokumenter' },
  { key: 'routes', label: 'Split' },
  { key: 'arbejdstimer', label: 'Arbejdstimer' },
  { key: 'analytics', label: 'Økonomi' },
  { key: 'prisberegner', label: 'Prisberegner' },
  { key: 'tilbud', label: 'Tilbud' },
  { key: 'produktsalg', label: 'Produktsalg' },
  { key: 'prismotor', label: 'Prismotor' },
  { key: 'ordreskemaer', label: 'Ordreskemaer' },
  { key: 'omkostninger', label: 'Omkostninger' },
  { key: 'settings', label: 'Indstillinger' },
  { key: 'brugere', label: 'Brugere' },
];

interface UserFormData {
  email: string;
  name: string;
  password: string;
  allowedMenuItems: string[];
  payrollAccess: boolean;
}

export default function OrgUsersList() {
  const [formOpen, setFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [createdCredentials, setCreatedCredentials] = useState<{ email: string; password: string } | null>(null);

  const { user: currentUser } = useAuth();
  const { data: users, isLoading, error, refetch } = useOrgUsers();
  const createUser = useCreateOrgUser();
  const updateUser = useUpdateOrgUser();
  const deleteUser = useDeleteOrgUser();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<UserFormData>({
    defaultValues: {
      email: '',
      name: '',
      password: '',
      allowedMenuItems: [],
      payrollAccess: true,
    },
  });

  const handleCreate = () => {
    setSelectedUser(null);
    setCreatedCredentials(null);
    reset({
      email: '',
      name: '',
      password: '',
      allowedMenuItems: [],
      payrollAccess: true,
    });
    setFormOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setCreatedCredentials(null);
    reset({
      email: user.email,
      name: user.name,
      password: '',
      allowedMenuItems: user.allowedMenuItems || [],
      payrollAccess: user.payrollAccess ?? true,
    });
    setFormOpen(true);
  };

  const handleDelete = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (userToDelete) {
      await deleteUser.mutateAsync(userToDelete.id);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleCloseDialog = () => {
    setFormOpen(false);
    setCreatedCredentials(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      // If all items are checked, store empty array (= all visible)
      const menuItems = data.allowedMenuItems.length === allMenuItems.length ? [] : data.allowedMenuItems;

      if (selectedUser) {
        await updateUser.mutateAsync({
          id: selectedUser.id,
          data: {
            email: data.email,
            name: data.name,
            password: data.password || undefined,
            allowedMenuItems: menuItems,
            payrollAccess: data.payrollAccess,
          },
        });
        setFormOpen(false);
      } else {
        const result = await createUser.mutateAsync({
          email: data.email,
          name: data.name,
          allowedMenuItems: menuItems,
          payrollAccess: data.payrollAccess,
        });
        setCreatedCredentials(result.credentials);
      }
      refetch();
    } catch {
      // Error handled by mutation
    }
  };

  const columns = [
    {
      id: 'name',
      label: 'Navn',
      minWidth: 150,
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 200,
    },
    {
      id: 'actions',
      label: '',
      align: 'right' as const,
      minWidth: 100,
      sortable: false,
      format: (_: unknown, row: User) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Tooltip title="Rediger">
            <IconButton size="small" onClick={() => handleEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.id !== currentUser?.id && (
            <Tooltip title="Slet">
              <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (error) {
    return (
      <Box>
        <Alert severity="error">Kunne ikke hente brugere.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Brugere</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreate}>
          Ny
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : users ? (
        <DataTable
          columns={columns}
          data={users}
          rowKey="id"
          searchPlaceholder="Søg bruger..."
        />
      ) : null}

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {createdCredentials ? (
          <>
            <DialogTitle>Bruger oprettet</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontFamily: 'monospace', lineHeight: 1.8 }}>
                    {`Log ind på insight.dora-apps.com\nEmail: ${createdCredentials.email}\nAdgangskode: ${createdCredentials.password}\n\nDu skal skifte adgangskode ved første login.`}
                  </Typography>
                </Paper>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyToClipboard(
                    `Log ind på insight.dora-apps.com\nEmail: ${createdCredentials.email}\nAdgangskode: ${createdCredentials.password}\n\nDu skal skifte adgangskode ved første login.`
                  )}
                >
                  Kopier alt
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Adgangskoden vises kun denne ene gang.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} variant="contained">Luk</Button>
            </DialogActions>
          </>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>
              {selectedUser ? 'Rediger Bruger' : 'Ny Bruger'}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {(createUser.error || updateUser.error) && (
                  <Alert severity="error">
                    {(createUser.error as Error)?.message || (updateUser.error as Error)?.message || 'Der opstod en fejl'}
                  </Alert>
                )}

                <TextField
                  label="Navn"
                  {...register('name', { required: 'Navn er påkrævet' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />

                <TextField
                  label="Email"
                  type="email"
                  {...register('email', {
                    required: 'Email er påkrævet',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Ugyldig email',
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />

                {selectedUser && (
                  <TextField
                    label="Adgangskode (lad tom for at beholde)"
                    type="password"
                    {...register('password', {
                      minLength: {
                        value: 8,
                        message: 'Adgangskode skal være mindst 8 tegn',
                      },
                    })}
                    error={!!errors.password}
                    helperText={errors.password?.message || 'Efterlad tom for at beholde nuværende'}
                  />
                )}

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Menupunkter
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Vælg hvilke sider brugeren kan se. Ingen valgt = alle synlige.
                  </Typography>
                  <Controller
                    name="allowedMenuItems"
                    control={control}
                    render={({ field }) => (
                      <FormGroup>
                        {allMenuItems.map(item => (
                          <FormControlLabel
                            key={item.key}
                            control={
                              <Checkbox
                                size="small"
                                checked={field.value.length === 0 || field.value.includes(item.key)}
                                onChange={(e) => {
                                  const current = field.value.length === 0
                                    ? allMenuItems.map(m => m.key)
                                    : [...field.value];
                                  if (e.target.checked) {
                                    field.onChange([...current, item.key]);
                                  } else {
                                    field.onChange(current.filter(k => k !== item.key));
                                  }
                                }}
                              />
                            }
                            label={<Typography variant="body2">{item.label}</Typography>}
                          />
                        ))}
                      </FormGroup>
                    )}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Løn-adgang
                  </Typography>
                  <Controller
                    name="payrollAccess"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Giver adgang til fanerne Lønoverblik og Indstillinger under Arbejdstimer
                          </Typography>
                        }
                      />
                    )}
                  />
                </Box>

                {!selectedUser && (
                  <Typography variant="caption" color="text.secondary">
                    En midlertidig adgangskode genereres automatisk. Brugeren skal skifte den ved første login.
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Annuller</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={createUser.isPending || updateUser.isPending}
              >
                {createUser.isPending || updateUser.isPending ? (
                  <CircularProgress size={24} />
                ) : selectedUser ? (
                  'Gem ændringer'
                ) : (
                  'Opret'
                )}
              </Button>
            </DialogActions>
          </form>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Slet bruger?</DialogTitle>
        <DialogContent>
          <Typography>
            Er du sikker på du vil slette <strong>{userToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuller</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Slet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
